import React, { useState, useCallback } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { JobPosition, ModalStep, FormData as CareerFormData } from "../types";
import ContentTag from "../../../components/content-tag/ContentTag";
import FileUploader from "../../../components/file-uploader/FileUploader";
import Input from "../../../components/form-item/Input";
import Stepper from "../../../components/stepper/Stepper";
import Modal, { type ModalAction } from "../../../components/modal/Modal";
import Textarea from "../../../components/form-item/Textarea";
import Select from "../../../components/form-item/Select";
import Toggle from "../../../components/form-item/Toggle";
// @ts-expect-error - JSファイルの型定義なし
import { HOSTNAME } from "../../../constant/host";
// @ts-expect-error - JSXファイルの型定義なし
import OsAudioToggle from "../../os/components/OsAudioToggle";

// ===== Step Content Components =====

const SectionTitle = styled.h2.attrs({
  className: "heading-spacing-1824",
})`
  margin-bottom: 8px;
  color: #000;
`;

const SectionSubtitle = styled.h3.attrs({
  className: "heading-spacing-18",
})`
  margin: 48px 0 8px;
  color: #000;
`;

const Description = styled.p.attrs({
  className: "bodytext u-line-height-150",
})`
  color: #000;
  white-space: pre-wrap;
`;

const RoleList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 12px 0 0 0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const RoleItem = styled.li.attrs({
  className: "bodytext-13",
})`
  padding: 4px 8px;
  background-color: var(--soremo-background);
  border: 1px solid var(--soremo-border);
  border-radius: 6px;
  color: #000;
`;

const WelcomeList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const WelcomeItem = styled.li.attrs({
  className: "bodytext u-row",
})`
  gap: 4px;
  color: #000;
`;

const WelcomeIcon = styled.span.attrs({
  className: "material-symbols-rounded",
})`
  font-size: 24px;
  color: var(--soremo-light-gray);
`;

const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

// Skill Category Styles (階層リスト用)
const SkillCategoryContainer = styled.div`
  margin-top: 16px;
`;

const SkillCategoryTitle = styled.h4.attrs({
  className: "bodytext",
})`
  color: #000;
  margin-bottom: 4px;
`;

const SkillSubcategoryTitle = styled.h5.attrs({
  className: "bodytext-13",
})`
  color: var(--soremo-light-gray);
  margin: 8px 0 0 0;
`;

const SkillList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 0 4px;
`;

const SkillItem = styled.li.attrs({
  className: "bodytext u-row",
})`
  gap: 8px;
  color: #000;
`;

const SkillDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #a7a8a9;
  flex-shrink: 0;
`;

// 条件セクション内のサブラベル（雇用形態 / 給与 / 勤務時間 / 休日・休暇 等）
const SubLabel = styled.h4.attrs({
  className: "bodytext-13",
})`
  color: var(--soremo-light-gray);
  margin: 24px 0 8px;

  &:first-of-type {
    margin-top: 0;
  }
`;

// 条件補足（試用期間・正社員登用ありなど）
const SubNotes = styled.p.attrs({
  className: "bodytext-13",
})`
  color: var(--soremo-light-gray);
  margin-top: 8px;
  white-space: pre-wrap;
`;

// 選考スケジュール（番号付きステップ）
const ProcessList = styled.ol`
  list-style: none;
  padding: 0;
  margin: 12px 0 0 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  counter-reset: process-step;
`;

const ProcessItem = styled.li.attrs({
  className: "bodytext u-row",
})`
  gap: 12px;
  color: #000;
  counter-increment: process-step;

  &::before {
    content: counter(process-step);
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: 1px solid var(--soremo-border);
    color: var(--soremo-light-gray);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: "din-2014-rounded-variable", sans-serif;
    font-size: 13px;
    line-height: 1;
  }
`;

// オフィス写真ギャラリー
const OfficeGallery = styled.div`
  margin-top: 16px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const OfficePhotoCard = styled.figure`
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const OfficePhotoImage = styled.img`
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid var(--soremo-border);
  display: block;
`;

const OfficePhotoCaption = styled.figcaption.attrs({
  className: "bodytext-13",
})`
  color: var(--soremo-light-gray);
  text-align: center;
`;

// Form Styles
const FormGroup = styled.div`
  margin-bottom: 24px;
`;

// SectionTitle 直下にフォーム / 確認フィールド群を配置する際の余白
const FieldsContainer = styled.div`
  margin-top: 32px;
`;

// submit 失敗時のエラー表示（danger 色は緑＝soremo の規約）
const SubmitErrorMessage = styled.p.attrs({
  className: "bodytext-13 u-text-green u-line-height-150",
})`
  margin-top: 16px;
`;

// 業務内容の箇条書き（description 直下に表示）
const ResponsibilitiesList = styled(WelcomeList)`
  margin-top: 16px;
`;

// チェックボックスグループ
const CheckboxGroupContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const CheckboxGroupItem = styled.div<{ $checked: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 4px 16px 4px 4px;
  border-radius: 4px;
  border: 1px solid ${props => props.$checked ? 'var(--soremo-blue)' : 'var(--soremo-border)'};
  background-color: ${props => props.$checked ? 'rgba(0, 154, 206, 0.1)' : '#fff'};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    border-color: var(--soremo-blue);
  }
  
`;

const CheckIcon = styled.span.attrs({
  className: "material-symbols-rounded",
})<{ $checked: boolean }>`
  font-size: 24px;
  color: ${props => props.$checked ? 'var(--soremo-blue)' : 'var(--soremo-border)'};
`;

// Confirm Styles
const ConfirmSection = styled.div`
  background-color: var(--soremo-background);
  border-radius: 6px;
  padding: 16px 24px;
  margin-bottom: 12px;
`;

const ConfirmLabel = styled.div.attrs({
  className: "bodytext-11",
})`
  color: var(--soremo-light-gray);
  margin-bottom: 4px;
`;

const ConfirmValue = styled.div.attrs({
  className: "bodytext",
})`
  color: #000;
  word-break: break-all;
`;

// Complete Styles
const CompleteContainer = styled.div`
  text-align: center;
  padding: 48px 0;
`;

const CompleteIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: var(--soremo-blue);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
`;

const CompleteIconText = styled.span.attrs({
  className: "material-symbols-rounded",
})`
  font-size: 40px;
  color: #fff;
`;

const CompleteTitle = styled.h2.attrs({
  className: "heading-spacing-1824",
})`
  color: var(--soremo-gray);
  margin-bottom: 16px;
`;

const CompleteMessage = styled.p.attrs({
  className: "bodytext",
})`
  color: var(--soremo-light-gray);
`;

// オーディオトグルのラッパー（フッターの上に配置）
const AudioToggleWrapper = styled.div`
  position: fixed;
  bottom: 88px;
  right: 24px;
  z-index: 10000;
  
  /* OsAudioToggleのposition: fixedを上書き */
  & > button {
    position: relative;
    bottom: auto;
    right: auto;
  }
`;

// ===== Component =====

interface CareersJobModalProps {
  job: JobPosition;
  isOpen: boolean;
  onClose: () => void;
  isAudioEnabled?: boolean;
  onAudioToggle?: () => void;
}

const STEPS: ModalStep[] = ['detail', 'form', 'confirm', 'complete'];
const STEP_LABELS = ['詳細', '入力', '確認', '完了'];

const CareersJobModal: React.FC<CareersJobModalProps> = ({ job, isOpen, onClose, isAudioEnabled, onAudioToggle }) => {
  const [currentStep, setCurrentStep] = useState<ModalStep>('detail');
  const [formData, setFormData] = useState<CareerFormData>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const currentStepIndex = STEPS.indexOf(currentStep);

  const handleInputChange = useCallback((fieldId: string, value: string | File | File[] | boolean) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }) as CareerFormData);
  }, []);

  const handleCheckboxGroupChange = useCallback((fieldId: string, option: string, checked: boolean) => {
    setFormData(prev => {
      const currentValues = (prev[fieldId] as string[]) || [];
      if (checked) {
        return { ...prev, [fieldId]: [...currentValues, option] };
      } else {
        return { ...prev, [fieldId]: currentValues.filter(v => v !== option) };
      }
    });
  }, []);

  const handleNext = useCallback(() => {
    if (currentStepIndex < STEPS.length - 1) {
      setCurrentStep(STEPS[currentStepIndex + 1]);
    }
  }, [currentStepIndex]);

  const handleBack = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStep(STEPS[currentStepIndex - 1]);
    }
  }, [currentStepIndex]);

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('position', job.title);

      // フィールドIDとラベルのマッピングを作成
      const fieldLabels: Record<string, string> = {};
      job.formFields.forEach(field => {
        fieldLabels[field.id] = field.label;
      });
      formDataToSend.append('field_labels', JSON.stringify(fieldLabels));

      // フォームデータをFormDataに変換
      Object.entries(formData).forEach(([key, value]) => {
        if (value instanceof File) {
          formDataToSend.append(key, value);
        } else if (Array.isArray(value)) {
          formDataToSend.append(key, JSON.stringify(value));
        } else if (typeof value === 'boolean') {
          formDataToSend.append(key, value ? '同意する' : '同意しない');
        } else {
          formDataToSend.append(key, String(value));
        }
      });

      const response = await fetch(`${HOSTNAME}/api/careers/apply/`, {
        method: 'POST',
        body: formDataToSend,
      });

      const result = await response.json();

      if (!response.ok || result.status === 'error') {
        throw new Error(result.message || '送信に失敗しました');
      }

      setCurrentStep('complete');
    } catch (error) {
      console.error('送信エラー:', error);
      setSubmitError(error instanceof Error ? error.message : '送信に失敗しました。もう一度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, job.title, job.formFields]);

  const handleClose = useCallback(() => {
    setCurrentStep('detail');
    setFormData({});
    setSubmitError(null);
    onClose();
  }, [onClose]);

  const isFormValid = useCallback(() => {
    return job.formFields
      .filter(field => field.required)
      .every(field => {
        const value = formData[field.id];
        if (field.type === 'checkbox') {
          return value === true;
        }
        if (field.type === 'checkboxGroup') {
          // チェックボックスグループは任意なのでバリデーション不要
          return true;
        }
        return value !== undefined && value !== '';
      });
  }, [job.formFields, formData]);

  const renderStepContent = () => {
    switch (currentStep) {
      case 'detail':
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <SectionTitle>{job.title}</SectionTitle>
            
            <SectionSubtitle>仕事内容</SectionSubtitle>
            <Description>{job.description}</Description>
            {job.responsibilities && job.responsibilities.length > 0 && (
              <ResponsibilitiesList>
                {job.responsibilities.map((item, index) => (
                  <WelcomeItem key={index}>
                    <WelcomeIcon>check</WelcomeIcon>
                    {item}
                  </WelcomeItem>
                ))}
              </ResponsibilitiesList>
            )}
            
            {job.roles.length > 0 && (
              <>
                <SectionSubtitle>担当役割</SectionSubtitle>
                <RoleList>
                  {job.roles.map((role, index) => (
                    <RoleItem key={index}>{role}</RoleItem>
                  ))}
                </RoleList>
              </>
            )}
            
            <SectionSubtitle>期待する成果</SectionSubtitle>
            <Description>{job.expectedOutcome}</Description>
            
            <SectionSubtitle>こんな人を歓迎します</SectionSubtitle>
            <WelcomeList>
              {job.welcome.map((item, index) => (
                <WelcomeItem key={index}>
                  <WelcomeIcon>check</WelcomeIcon>
                  {item}
                </WelcomeItem>
              ))}
            </WelcomeList>
            
            {/* 階層構造のスキルカテゴリ表示 */}
            {job.skillCategories && job.skillCategories.length > 0 && (
              <>
                <SectionSubtitle>求めるスキル・経験</SectionSubtitle>
                {job.skillCategories.map((category, categoryIndex) => (
                  <SkillCategoryContainer key={categoryIndex}>
                    <SkillCategoryTitle>{category.title}</SkillCategoryTitle>
                    
                    {/* サブカテゴリがある場合 */}
                    {category.subcategories && category.subcategories.map((subcategory, subIndex) => (
                      <div key={subIndex}>
                        <SkillSubcategoryTitle>{subcategory.title}</SkillSubcategoryTitle>
                        <SkillList>
                          {subcategory.items.map((item, itemIndex) => (
                            <SkillItem key={itemIndex}>
                              <SkillDot />
                              {item}
                            </SkillItem>
                          ))}
                        </SkillList>
                      </div>
                    ))}
                    
                    {/* サブカテゴリなしの直接項目 */}
                    {category.items && category.items.length > 0 && (
                      <SkillList>
                        {category.items.map((item, itemIndex) => (
                          <SkillItem key={itemIndex}>
                            <SkillDot />
                            {item}
                          </SkillItem>
                        ))}
                      </SkillList>
                    )}
                  </SkillCategoryContainer>
                ))}
              </>
            )}
            
            {job.conditions && (
              <>
                <SectionSubtitle>条件</SectionSubtitle>

                {job.tags && job.tags.length > 0 && (
                  <>
                    <SubLabel>雇用形態</SubLabel>
                    <TagContainer>
                      {job.tags.map((tag, index) => (
                        <ContentTag key={index}>{tag}</ContentTag>
                      ))}
                    </TagContainer>
                    {job.conditions.contractNotes && job.conditions.contractNotes.length > 0 && (
                      <SubNotes>{job.conditions.contractNotes.join(' / ')}</SubNotes>
                    )}
                  </>
                )}

                {job.conditions.salary && (
                  <>
                    <SubLabel>給与</SubLabel>
                    <Description>{job.conditions.salary}</Description>
                  </>
                )}

                {job.conditions.workingHours && (
                  <>
                    <SubLabel>勤務時間</SubLabel>
                    <Description>{job.conditions.workingHours}</Description>
                  </>
                )}

                <SubLabel>福利厚生</SubLabel>
                <WelcomeList>
                  {job.conditions.benefits.map((benefit, index) => (
                    <WelcomeItem key={index}>
                      <WelcomeIcon>check</WelcomeIcon>
                      {benefit}
                    </WelcomeItem>
                  ))}
                </WelcomeList>

                {job.conditions.holidays && job.conditions.holidays.length > 0 && (
                  <>
                    <SubLabel>休日・休暇</SubLabel>
                    <WelcomeList>
                      {job.conditions.holidays.map((holiday, index) => (
                        <WelcomeItem key={index}>
                          <WelcomeIcon>check</WelcomeIcon>
                          {holiday}
                        </WelcomeItem>
                      ))}
                    </WelcomeList>
                  </>
                )}

                <SubLabel>勤務地</SubLabel>
                <Description>{job.conditions.location}</Description>
                {job.officePhotos && job.officePhotos.length > 0 && (
                  <OfficeGallery>
                    {job.officePhotos.map((photo) => (
                      <OfficePhotoCard key={photo.src}>
                        <OfficePhotoImage src={photo.src} alt={photo.label} loading="lazy" />
                        <OfficePhotoCaption>{photo.label}</OfficePhotoCaption>
                      </OfficePhotoCard>
                    ))}
                  </OfficeGallery>
                )}
              </>
            )}

            {job.selectionProcess && job.selectionProcess.length > 0 && (
              <>
                <SectionSubtitle>選考スケジュール</SectionSubtitle>
                <ProcessList>
                  {job.selectionProcess.map((step, index) => (
                    <ProcessItem key={index}>{step}</ProcessItem>
                  ))}
                </ProcessList>
              </>
            )}

            {job.notes && job.notes.length > 0 && (
              <>
                <SectionSubtitle>注意事項</SectionSubtitle>
                <WelcomeList>
                  {job.notes.map((note, index) => (
                    <WelcomeItem key={index}>
                      <WelcomeIcon>check</WelcomeIcon>
                      {note}
                    </WelcomeItem>
                  ))}
                </WelcomeList>
              </>
            )}
          </motion.div>
        );

      case 'form':
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <SectionTitle>応募フォーム</SectionTitle>
            <Description>以下の項目を入力してください。</Description>
            
            <FieldsContainer>
              {job.formFields.map(field => (
                <FormGroup key={field.id}>
                  {field.type === 'text' || field.type === 'email' || field.type === 'tel' ? (
                    <Input
                      label={field.label}
                      option={field.required ? 'required' : 'optional'}
                      type={field.type}
                      placeholder={field.placeholder}
                      value={(formData[field.id] as string) || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange(field.id, e.target.value)}
                    />
                  ) : field.type === 'textarea' ? (
                    <Textarea
                      label={field.label}
                      option={field.required ? 'required' : 'optional'}
                      placeholder={field.placeholder}
                      value={(formData[field.id] as string) || ''}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleInputChange(field.id, e.target.value)}
                      rows={5}
                    />
                  ) : field.type === 'select' ? (
                    <Select
                      label={field.label}
                      value={(formData[field.id] as string) || ''}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleInputChange(field.id, e.target.value)}
                      options={[
                        { value: '', name: '選択してください' },
                        ...(field.options?.map(option => ({ value: option, name: option })) || [])
                      ]}
                    />
                  ) : field.type === 'file' ? (
                    <div className="u-col u-gap4 u-w100">
                      <label className="bodytext-13 u-line-height-100 u-pl4">
                        {field.label}
                        <span className={`label8 u-pl8 ${field.required ? 'u-text-blue' : 'u-text-light-gray'}`}>
                          {field.required ? '必須' : '任意'}
                        </span>
                      </label>
                      <FileUploader
                        files={formData[field.id] ? [formData[field.id] as File] : []}
                        onChange={(files) => {
                          if (files.length > 0) {
                            handleInputChange(field.id, files[0]);
                          } else {
                            handleInputChange(field.id, '' as unknown as File);
                          }
                        }}
                        multiple={false}
                        height={120}
                        placeholder="クリックまたはファイルをドラッグ & ドロップ"
                      />
                    </div>
                  ) : field.type === 'checkbox' ? (
                    <Toggle
                      value={(formData[field.id] as boolean) || false}
                      onClick={() => handleInputChange(field.id, !(formData[field.id] as boolean))}
                    >
                      {field.id === 'agree' ? (
                        <span className="bodytext">
                          <a 
                            href="/privacypolicy" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            style={{ color: 'var(--soremo-blue)', textDecoration: 'none', fontSize: 'inherit' }}
                          >
                            個人情報の取り扱い
                          </a>
                          について同意します
                        </span>
                      ) : (
                        <span className="bodytext">{field.placeholder || field.label}</span>
                      )}
                    </Toggle>
                  ) : field.type === 'checkboxGroup' ? (
                    <div className="u-col u-gap4 u-w100">
                      <label className="bodytext-13 u-line-height-100 u-pl4">
                        {field.label}
                        <span className={`label8 u-pl8 ${field.required ? 'u-text-blue' : 'u-text-light-gray'}`}>
                          {field.required ? '必須' : '任意'}
                        </span>
                      </label>
                      <CheckboxGroupContainer>
                        {field.options?.map((option, index) => {
                          const isChecked = ((formData[field.id] as string[]) || []).includes(option);
                          return (
                            <CheckboxGroupItem key={index} $checked={isChecked} onClick={() => handleCheckboxGroupChange(field.id, option, !isChecked)}>
                              <CheckIcon $checked={isChecked}>
                                {isChecked ? 'check_box' : 'check_box_outline_blank'}
                              </CheckIcon>
                              <span className="bodytext-13">{option}</span>
                            </CheckboxGroupItem>
                          );
                        })}
                      </CheckboxGroupContainer>
                    </div>
                  ) : null}
                </FormGroup>
              ))}
            </FieldsContainer>
          </motion.div>
        );

      case 'confirm':
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <SectionTitle>入力内容の確認</SectionTitle>
            <Description>以下の内容で応募します。よろしいですか？</Description>
            
            <FieldsContainer>
              <ConfirmSection>
                <ConfirmLabel>応募職種</ConfirmLabel>
                <ConfirmValue>{job.title}</ConfirmValue>
              </ConfirmSection>
              
              {job.formFields.map(field => {
                const value = formData[field.id];
                let displayValue = '';
                
                if (field.type === 'file' && value instanceof File) {
                  displayValue = value.name;
                } else if (field.type === 'checkbox') {
                  displayValue = value ? '同意する' : '同意しない';
                } else if (field.type === 'checkboxGroup') {
                  const selectedItems = (value as string[]) || [];
                  displayValue = selectedItems.length > 0 ? selectedItems.join('、') : '未選択';
                } else {
                  displayValue = (value as string) || '未入力';
                }
                
                return (
                  <ConfirmSection key={field.id}>
                    <ConfirmLabel>{field.label}</ConfirmLabel>
                    <ConfirmValue>{displayValue}</ConfirmValue>
                  </ConfirmSection>
                );
              })}

              {submitError && (
                <SubmitErrorMessage>{submitError}</SubmitErrorMessage>
              )}
            </FieldsContainer>
          </motion.div>
        );

      case 'complete':
        return (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <CompleteContainer>
              <CompleteIcon>
                <CompleteIconText>check</CompleteIconText>
              </CompleteIcon>
              <CompleteTitle>応募が完了しました</CompleteTitle>
              <CompleteMessage>
                ご応募いただきありがとうございます。
                <br />
                内容を確認の上、担当者よりご連絡いたします。
                <br />
                今しばらくお待ちください。
              </CompleteMessage>
            </CompleteContainer>
          </motion.div>
        );

      default:
        return null;
    }
  };

  const getPrimaryAction = (): ModalAction | undefined => {
    switch (currentStep) {
      case "detail":
        return {
          label: "応募する",
          onClick: handleNext,
          rightIcon: "chevron_right",
        };
      case "form":
        return {
          label: "次へ",
          onClick: handleNext,
          disabled: !isFormValid(),
          rightIcon: "chevron_right",
        };
      case "confirm":
        return {
          label: "送信",
          onClick: handleSubmit,
          loading: isSubmitting,
          rightIcon: isSubmitting ? undefined : "chevron_right",
        };
      case "complete":
        return {
          label: "閉じる",
          onClick: handleClose,
        };
      default:
        return undefined;
    }
  };

  const getSecondaryAction = (): ModalAction | undefined => {
    switch (currentStep) {
      case "form":
      case "confirm":
        return {
          label: "戻る",
          onClick: handleBack,
          disabled: isSubmitting,
          leftIcon: "chevron_left",
        };
      default:
        return undefined;
    }
  };

  if (!isOpen) return null;

  const canGoBackToTop = currentStep !== "complete" && !isSubmitting;
  const showStepper = currentStep !== "complete";

  return (
    <Modal
      variant="fullscreen"
      onBack={canGoBackToTop ? handleClose : undefined}
      headerExtras={
        showStepper && (
          <Stepper
            stepLabels={STEP_LABELS}
            current={currentStepIndex + 1}
            showCheckOnComplete={true}
          />
        )
      }
      bodyWidth="reading"
      primaryAction={getPrimaryAction()}
      secondaryAction={getSecondaryAction()}
    >
      <AnimatePresence mode="wait">{renderStepContent()}</AnimatePresence>
      {onAudioToggle && (
        <AudioToggleWrapper>
          <OsAudioToggle
            isPlaying={isAudioEnabled}
            onToggle={onAudioToggle}
            isVisible={true}
          />
        </AudioToggleWrapper>
      )}
    </Modal>
  );
};

export default CareersJobModal;
